import { useCallback, useEffect, useRef, useState } from 'react'
import { Download, Trash2 } from 'lucide-react'
import { ApiError, type UploadInfo } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { platformApi } from '../api'
import { formatSize, isAllowedExtension, saveBlob } from '../lib/files'
import type { FileRecord } from '../types'

interface Props {
    id: string
    /** The question key (the upload target). */
    fieldKey: string
    value: unknown
    onChange: (v: string[] | string) => void
    submissionId?: string
    readOnly?: boolean
    invalid?: boolean
}

function LinkInput({ id, value, onChange, invalid }: Pick<Props, 'id' | 'value' | 'onChange' | 'invalid'>) {
    return (
        <div className="flex flex-col gap-1.5">
            <Input
                id={id}
                type="url"
                className="max-w-md"
                aria-invalid={invalid}
                placeholder="https://… link to the document"
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => onChange(e.target.value)}
            />
            <span className="text-xs text-muted-foreground">Paste a share link; file upload is not available yet.</span>
        </div>
    )
}

/** Upload control for a 'file' question; falls back to a link input when uploads are off. */
export function FileInput(props: Props) {
    const { user } = useAuth()
    const uploads = user?.uploads
    if (!uploads?.enabled) return <LinkInput {...props} />
    if (typeof props.value === 'string' && props.value.trim() !== '') return <LinkInput {...props} />
    return <UploadControl {...props} uploads={uploads} />
}

function UploadControl({ id, fieldKey, value, onChange, submissionId, readOnly, invalid, uploads }: Props & {
    uploads: UploadInfo
}) {
    const ids = Array.isArray(value) ? (value as string[]) : []
    const [records, setRecords] = useState<FileRecord[]>([])
    const [busyName, setBusyName] = useState<string | null>(null)
    const [removing, setRemoving] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [dragging, setDragging] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const disabled = !submissionId || readOnly

    useEffect(() => {
        if (!submissionId) return
        let cancelled = false
        platformApi.listFiles(submissionId)
            .then((r) => { if (!cancelled) setRecords(r.items) })
            .catch(() => undefined)
        return () => { cancelled = true }
    }, [submissionId])

    const upload = useCallback(async (picked: File[]) => {
        if (!submissionId || picked.length === 0) return
        setError(null)
        let current = ids
        for (const file of picked) {
            if (current.length >= uploads.maxPerField) {
                setError(`You can attach at most ${uploads.maxPerField} ${uploads.maxPerField === 1 ? 'file' : 'files'} to this question.`)
                break
            }
            if (!isAllowedExtension(file.name, uploads.allowed)) {
                setError(`${file.name}: this file type is not accepted. Allowed: ${uploads.allowed.join(', ')}.`)
                break
            }
            if (file.size > uploads.maxMB * 1024 * 1024) {
                setError(`${file.name}: file is larger than ${uploads.maxMB} MB.`)
                break
            }
            setBusyName(file.name)
            try {
                const rec = await platformApi.uploadFile(submissionId, fieldKey, file)
                setRecords((r) => [...r, rec])
                current = [...current, rec.id]
                onChange(current)
            } catch (e) {
                setError(e instanceof ApiError || e instanceof Error ? `${file.name}: ${e.message}` : 'Upload failed')
                break
            } finally {
                setBusyName(null)
            }
        }
        if (inputRef.current) inputRef.current.value = ''
    }, [submissionId, ids, uploads, fieldKey, onChange])

    const remove = async (fid: string) => {
        if (!submissionId) return
        setError(null)
        setRemoving(fid)
        try {
            await platformApi.deleteFile(submissionId, fid)
            setRecords((r) => r.filter((x) => x.id !== fid))
            onChange(ids.filter((x) => x !== fid))
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not remove the file')
        } finally {
            setRemoving(null)
        }
    }

    const download = async (rec: FileRecord) => {
        if (!submissionId) return
        setError(null)
        try {
            const { blob, filename } = await platformApi.downloadFile(submissionId, rec.id)
            saveBlob(blob, filename || rec.name)
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not download the file')
        }
    }

    if (!submissionId) {
        return (
            <div className="flex max-w-xl items-center rounded-md border border-dashed bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
                File uploads are shown here
            </div>
        )
    }

    const busy = busyName !== null
    const full = ids.length >= uploads.maxPerField
    const attached = ids.map((fid) => records.find((r) => r.id === fid)).filter((r): r is FileRecord => Boolean(r))

    return (
        <div className="flex max-w-xl flex-col gap-3">
            {!readOnly && (
                <div
                    className={cn(
                        'flex flex-col items-start gap-2 rounded-md border border-dashed px-4 py-4 text-sm',
                        dragging && 'bg-muted/50',
                        invalid && 'border-destructive',
                    )}
                    onDragOver={(e) => { e.preventDefault(); if (!disabled && !busy && !full) setDragging(true) }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault()
                        setDragging(false)
                        if (!disabled && !busy && !full) void upload(Array.from(e.dataTransfer.files))
                    }}
                >
                    <div className="flex flex-wrap items-center gap-3">
                        <span>Drag a file here or choose a file</span>
                        <Button type="button" variant="outline" size="sm" disabled={busy || full}
                            onClick={() => inputRef.current?.click()}>
                            Choose file
                        </Button>
                        <input
                            ref={inputRef}
                            id={id}
                            type="file"
                            className="sr-only"
                            tabIndex={-1}
                            multiple={uploads.maxPerField - ids.length > 1}
                            accept={uploads.allowed.join(',')}
                            onChange={(e) => void upload(Array.from(e.target.files ?? []))}
                        />
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {uploads.allowed.join(', ')} · up to {uploads.maxMB} MB each · up to {uploads.maxPerField} {uploads.maxPerField === 1 ? 'file' : 'files'}
                    </span>
                </div>
            )}

            {(attached.length > 0 || busy) && (
                <ul className="flex flex-col divide-y rounded-md border text-sm">
                    {attached.map((r) => (
                        <li key={r.id} className="flex items-center gap-3 px-3 py-2">
                            <span className="min-w-0 flex-1 truncate">{r.name}</span>
                            <span className="text-xs text-muted-foreground">{formatSize(r.size_bytes)}</span>
                            <Button type="button" variant="ghost" size="sm" aria-label={`Download ${r.name}`}
                                onClick={() => void download(r)}>
                                <Download data-icon="inline-start" />Download
                            </Button>
                            {!readOnly && (
                                <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${r.name}`}
                                    disabled={busy || removing !== null} onClick={() => void remove(r.id)}>
                                    {removing === r.id ? <Spinner data-icon="inline-start" /> : <Trash2 data-icon="inline-start" />}
                                    Remove
                                </Button>
                            )}
                        </li>
                    ))}
                    {busy && (
                        <li className="flex items-center gap-3 px-3 py-2 text-muted-foreground">
                            <Spinner />
                            <span className="min-w-0 flex-1 truncate">Uploading {busyName}</span>
                        </li>
                    )}
                </ul>
            )}

            {error && <FieldError>{error}</FieldError>}
        </div>
    )
}
